import { MoreHorizontal, type LucideIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useActiveFinish } from '../platform.tsx';

export interface RowAction {
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  /** Ação que apaga ou desfaz: em vermelho. */
  danger?: boolean;
}

/**
 * As ações de uma linha de tabela (ADR 0133), no jeito do acabamento (`rowActions`): um botão
 * "mais" que abre a lista, os ícones sempre à vista, ou os ícones só ao passar o mouse na linha.
 * Cada ícone tem o nome da ação para leitores de tela e no "title".
 */
export function RowActions({ actions }: { actions: readonly RowAction[] }) {
  const { t } = useTranslation();
  const { rowActions } = useActiveFinish();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  const iconBtn = 'rounded-md p-1.5 text-muted hover:bg-paper hover:text-ink';
  if (rowActions !== 'menu') {
    return (
      <div className="ui-row-actions flex justify-end gap-0.5">
        {actions.map((a) => (
          <button
            key={a.label}
            type="button"
            title={a.label}
            aria-label={a.label}
            onClick={a.onClick}
            className={`${iconBtn} ${a.danger ? 'hover:text-danger' : ''}`}
          >
            <a.icon size={16} aria-hidden />
          </button>
        ))}
      </div>
    );
  }
  return (
    <div ref={box} className="relative flex justify-end">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('common.moreActions')}
        title={t('common.moreActions')}
        onClick={() => setOpen((o) => !o)}
        className={iconBtn}
      >
        <MoreHorizontal size={16} aria-hidden />
      </button>
      {open && (
        <ul
          role="menu"
          className="ui-panel absolute top-full right-0 z-20 mt-1 flex min-w-44 flex-col rounded-md bg-sheet p-1 text-sm"
        >
          {actions.map((a) => (
            <li key={a.label} role="none">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  a.onClick();
                }}
                className={`flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-left hover:bg-paper ${a.danger ? 'text-danger' : ''}`}
              >
                <a.icon size={15} aria-hidden className="ui-icon-support" />
                {a.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
